import React, { FC, useEffect, useState } from 'react';
import { Modal } from '@design-system/modal';
import { PromoFormProps } from '@design-system/promo-form-block';
import { getLocalStorageData, setLocalStorageData } from '@phx-husky/local-storage';

import { AirwickNewsletterForm } from './elements/airwick-newsletter-form';
import { AirwickNewsletterThankYou } from './elements/airwick-newsletter-thank-you';
import { AirwickNewsletterPopupProps } from './models';

import './airwick-newsletter-popup.scss';

const NEWSLETTER_POPUP_DEFAULT_TIMEOUT_IN_SECONDS = 5;
const NEWSLETTER_POPUP_DISPLAYED = 'airwick-newsletter-popup-displayed';
const DOCUMENT_LOCK_CLASSNAME = 'cds-scroll-locked';

export const AirwickNewsletterPopup: FC<AirwickNewsletterPopupProps> = ({
  headline,
  description,
  thankYou,
  form,
  popupAppearanceTimeoutInSeconds,
  onFormSubmitCallback,
}) => {
  const [popupVisible, setPopupVisible] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  useEffect(() => {
    const localStorageValue = getLocalStorageData(NEWSLETTER_POPUP_DISPLAYED);
    if (!localStorageValue) {
      const timeoutInMilliseconds =
        (popupAppearanceTimeoutInSeconds ?? NEWSLETTER_POPUP_DEFAULT_TIMEOUT_IN_SECONDS) * 1000;

      const timeout = setTimeout(() => {
        setPopupVisible(true);
      }, timeoutInMilliseconds);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [popupAppearanceTimeoutInSeconds]);

  useEffect(() => {
    if (popupVisible) {
      document.documentElement.classList.add(DOCUMENT_LOCK_CLASSNAME);
    } else {
      document.documentElement.classList.remove(DOCUMENT_LOCK_CLASSNAME);
    }
  }, [popupVisible]);

  const onClose = () => {
    setPopupVisible(false);
    setShowThankYou(false);
    setLocalStorageData(NEWSLETTER_POPUP_DISPLAYED, true);
  };

  const onCloseThankYou = () => {
    onClose();

    const { redirectUrl } = thankYou.closeButton;

    if (redirectUrl) {
      window.open(redirectUrl, '_self');
    }
  };

  const onFormSent: PromoFormProps['onSuccessCallback'] = () => {
    setShowThankYou(true);
    setLocalStorageData(NEWSLETTER_POPUP_DISPLAYED, true);

    if (typeof onFormSubmitCallback === 'function') {
      onFormSubmitCallback();
    }
  };

  return (
    <Modal isShowing={popupVisible} closeModal={onClose} className="airwick-newsletter-modal">
      <div className="newsletter-popup-container">
        {showThankYou ? (
          <AirwickNewsletterThankYou {...thankYou} onCloseThankYou={onCloseThankYou} />
        ) : (
          <AirwickNewsletterForm
            headline={headline}
            description={description}
            form={form}
            onFormSent={onFormSent}
          />
        )}
      </div>
    </Modal>
  );
};
